import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../config';
import { FaLock, FaCheckCircle, FaExclamationCircle, FaUserCircle, FaShieldAlt } from 'react-icons/fa';

const ProfileTab = ({ user }) => {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: '',
    });
    const [status, setStatus] = useState({ type: '', message: '' });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/profile_actions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ action: 'get_profile' }),
            });
            const data = await res.json();
            if (data.success) {
                setProfile(data.data.profile);
            }
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = async (e) => {
        e.preventDefault();
        if (passwordData.new_password !== passwordData.confirm_password) {
            setStatus({ type: 'error', message: 'Credentials mismatch' });
            return;
        }

        setSubmitting(true);
        setStatus({ type: '', message: '' });

        try {
            const res = await fetch(`${API_BASE_URL}/profile_actions.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    action: 'change_password',
                    current_password: passwordData.current_password,
                    new_password: passwordData.new_password,
                }),
            });
            const data = await res.json();
            if (data.success) {
                setStatus({ type: 'success', message: 'Security credentials updated' });
                setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
            } else {
                setStatus({ type: 'error', message: data.message });
            }
        } catch (error) {
            setStatus({ type: 'error', message: 'Signal failure' });
        } finally {
            setSubmitting(false);
        }
    };

    const getLoyaltyInfo = (count) => {
        if (count >= 25) return { name: 'Platinum Operative', color: 'bg-slate-900', textColor: 'text-white', icon: '💎', next: null };
        if (count >= 15) return { name: 'Gold Specialist', color: 'bg-white', border: 'border-amber-200', textColor: 'text-slate-900', icon: '🏆', next: 25 };
        if (count >= 7) return { name: 'Elite Guardian', color: 'bg-white', border: 'border-slate-200', textColor: 'text-slate-900', icon: '🛡️', next: 15 };
        return { name: 'Field Agent', color: 'bg-white', border: 'border-slate-100', textColor: 'text-slate-900', icon: '🔰', next: 7 };
    };

    const loyalty = getLoyaltyInfo(profile?.attendance_count || 0);
    const progress = loyalty.next ? ((profile?.attendance_count || 0) / loyalty.next) * 100 : 100;

    if (loading) {
        return (
            <div className="flex justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto space-y-6 animate-premium">
            {/* Professional Tier Card */}
            <div className={`relative overflow-hidden rounded-3xl p-8 border ${loyalty.border || 'border-slate-100'} bg-white shadow-[0_8px_30px_rgb(0,0,0,0.02)]`}>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
                    <div className="flex items-center gap-6">
                        <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl bg-slate-50 shadow-sm border border-slate-100">
                            {loyalty.icon}
                        </div>
                        <div>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] mb-1 text-indigo-500">Current Achievement Rank</p>
                            <h2 className="text-2xl font-black tracking-tight text-slate-900 uppercase">{loyalty.name}</h2>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                                {profile?.attendance_count || 0} Professional Contributions
                            </p>
                        </div>
                    </div>

                    {loyalty.next && (
                        <div className="flex-1 max-w-xs">
                            <div className="flex justify-between items-end mb-2">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Next Tier Progress</span>
                                <span className="text-xs font-black text-slate-900">{profile?.attendance_count || 0} / {loyalty.next}</span>
                            </div>
                            <div className="h-2 bg-slate-50 rounded-full overflow-hidden border border-slate-100/50">
                                <div
                                    className="h-full bg-indigo-600 transition-all duration-1000 ease-out shadow-[0_0_10px_rgba(79,70,229,0.3)]"
                                    style={{ width: `${Math.min(progress, 100)}%` }}
                                ></div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="bg-white rounded-3xl border border-slate-100 p-8 md:p-10 shadow-[0_4px_20px_rgb(0,0,0,0.02)]">
                <div className="flex flex-col md:flex-row md:items-center gap-8 mb-10 pb-10 border-b border-slate-50">
                    <div className="w-20 h-20 bg-slate-900 rounded-2xl flex items-center justify-center text-2xl font-black text-white shadow-xl shadow-slate-900/10">
                        {profile?.name?.charAt(0) || 'U'}
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-2xl font-black text-slate-900 tracking-tight uppercase">{profile?.name}</h2>
                        <p className="text-sm font-bold text-slate-400 lowercase">{profile?.email}</p>
                        <div className="flex gap-2 pt-2">
                            <span className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[9px] font-black uppercase tracking-widest border border-indigo-100/30">
                                {profile?.role?.replace('_', ' ')}
                            </span>
                            <span className="px-3 py-1.5 bg-slate-50 text-slate-500 border border-slate-100 rounded-lg text-[9px] font-black uppercase tracking-widest">
                                {profile?.position || 'Member'}
                            </span>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                    <div className="space-y-8">
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 border-l-2 border-indigo-500 pl-3">Organization Details</p>
                            <div className="grid grid-cols-1 gap-6">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Sector</p>
                                        <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{profile?.district_name || '-'}</p>
                                    </div>
                                    <div className="p-4 bg-slate-50 rounded-xl border border-slate-100">
                                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Sub-Sector</p>
                                        <p className="text-xs font-black text-slate-900 uppercase tracking-tight">{profile?.sub_district_name || '-'}</p>
                                    </div>
                                </div>

                                <div className="p-5 bg-slate-50/50 rounded-xl border border-slate-100">
                                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2">Base Club</p>
                                    <p className="text-base font-black text-slate-900 uppercase tracking-tight">
                                        {profile?.club_name || '-'}
                                    </p>
                                    {profile?.lion_ID && (
                                        <p className="text-[10px] font-bold text-indigo-500 mt-2 tracking-widest">LION ID: {profile.lion_ID}</p>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    {profile?.admission_number && (
                                        <div className="p-4 bg-white border border-slate-100 rounded-xl">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Admission No</p>
                                            <p className="text-xs font-black text-slate-900">{profile.admission_number}</p>
                                        </div>
                                    )}
                                    {profile?.nic_number && (
                                        <div className="p-4 bg-white border border-slate-100 rounded-xl">
                                            <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-1">Identity (NIC)</p>
                                            <p className="text-xs font-black text-slate-900">{profile.nic_number}</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-8">
                        <div>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-6 border-l-2 border-rose-500 pl-3">Security & Access</p>
                            <form onSubmit={handlePasswordChange} className="space-y-4">
                                {status.message && (
                                    <div className={`p-4 rounded-xl flex items-center gap-3 text-[10px] font-black uppercase tracking-widest mb-4 transition-all ${status.type === 'success' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'bg-rose-50 text-rose-600 border border-rose-100'
                                        }`}>
                                        {status.type === 'success' ? <FaCheckCircle /> : <FaExclamationCircle />}
                                        {status.message}
                                    </div>
                                )}

                                <div className="space-y-3">
                                    <div className="relative">
                                        <input
                                            type="password"
                                            required
                                            className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-slate-500/5 focus:border-slate-900 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal"
                                            value={passwordData.current_password}
                                            onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                                            placeholder="Current Password"
                                        />
                                    </div>

                                    <div className="relative">
                                        <input
                                            type="password"
                                            required
                                            className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-slate-500/5 focus:border-slate-900 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal"
                                            value={passwordData.new_password}
                                            onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                                            placeholder="New Password"
                                        />
                                    </div>

                                    <div className="relative">
                                        <input
                                            type="password"
                                            required
                                            className="w-full px-5 py-3.5 bg-slate-50 border border-slate-100 rounded-xl focus:bg-white focus:ring-4 focus:ring-slate-500/5 focus:border-slate-900 outline-none transition-all text-sm font-bold text-slate-900 placeholder:text-slate-300 placeholder:font-normal"
                                            value={passwordData.confirm_password}
                                            onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                                            placeholder="Verify Password"
                                        />
                                    </div>
                                </div>

                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full py-4 bg-slate-900 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-slate-900/10 hover:bg-black hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:translate-y-0 flex items-center justify-center gap-3 mt-4"
                                >
                                    {submitting ? 'Authenticating...' : 'Update Security Credentials'}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileTab;
